<template>
  <div class="vr-mask-container">
    <div class="control-panel">
      <h2>VR藏戏脸谱体验 <span class="title-tibetan">VR བོད་ཞབས་བརྙན་འབག་ཉམས་མྱོང།</span></h2>
      <p class="sub-title">基于TensorFlow.js人脸检测，实时将藏戏面具渲染到用户面部<br><span class="tibetan">TensorFlow.js གདོང་འཚོལ་ཐོག་བོད་ཞབས་འབག་པ་སཀུ་གདོང་ལ་སྟོན།</span></p>
      <div class="controls">
        <el-select v-model="selectedMask" placeholder="选择脸谱" @change="changeMask">
          <el-option
            v-for="mask in masks"
            :key="mask.id"
            :label="mask.name"
            :value="mask.id"
          />
        </el-select>
        
        <el-button type="primary" @click="startCamera" v-if="!cameraStarted">
          <el-icon><VideoCamera /></el-icon>
          开启摄像头 <span class="btn-tibetan">པར་ཆས་ཕྱེ།</span>
        </el-button>
        <el-button type="danger" @click="stopCamera" v-else>
          <el-icon><VideoCameraFilled /></el-icon>
          关闭摄像头 <span class="btn-tibetan">པར་ཆས་བསདམས།</span>
        </el-button>
        
        <el-button type="success" @click="capturePhoto" :disabled="!cameraStarted">
          <el-icon><Camera /></el-icon>
          拍照保存 <span class="btn-tibetan">པར་ལེན།</span>
        </el-button>
      </div>
    </div>
    
    <div class="display-area">
      <div class="video-wrapper">
        <!-- video不可见但仍渲染，canvas负责显示画面+面具叠加 -->
        <video ref="videoRef" autoplay playsinline muted class="hidden-video"></video>
        <canvas ref="canvasRef"></canvas>
        <div class="no-camera" v-if="!cameraStarted">
          <el-icon :size="60"><VideoCamera /></el-icon>
          <p>请点击“开启摄像头”开始体验<br><span class="tibetan">པར་ཆས་ཕྱེ། ལ་བརད་དེ་ཉམས་མྱོང་མགོ།</span></p>
        </div>
      </div>
      
      <div class="mask-preview" v-if="currentMask">
        <h3>当前脸谱预览 <span class="preview-tibetan">ད་ལྟའི་འབག་འདྲ།</span></h3>
        <img :src="currentMask.image" :alt="currentMask.name">
        <h4>{{ currentMask.name }}</h4>
        <div class="mask-description">{{ currentMask.description }}</div>
      </div>
    </div>
    
    <el-dialog v-model="photoDialogVisible" title="拍照成功" width="600px">
      <img :src="capturedPhoto" style="width: 100%;" alt="拍照">
      <template #footer>
        <el-button @click="photoDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="downloadPhoto">下载照片</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import { VideoCamera, VideoCameraFilled, Camera } from '@element-plus/icons-vue'
import { getMasks } from '@/api'
import * as blazeface from '@tensorflow-models/blazeface'
import '@tensorflow/tfjs-backend-webgl'

import whiteMaskImg from '@/assets/images/white.png'
import blueMaskImg from '@/assets/images/blue.jpg'
import blackMaskImg from '@/assets/images/black.jpg'
import yellowMaskImg from '@/assets/images/yellow.jpg'
import redMaskImg from '@/assets/images/red.jpg'
import whitejdkImg from '@/assets/images/whitejdk.png'
import whitehyImg from '@/assets/images/whitehy.png'
import hsmjImg from '@/assets/images/hsmj.png'
import whitebsjzImg from '@/assets/images/whitebsjz.png'
import slsImg from '@/assets/images/sls.png'
import lsmjImg from '@/assets/images/lsmj.png'
import slsmjjzkImg from '@/assets/images/slsmjjzk.png'
import lsmjjykImg from '@/assets/images/lsmjjyk.png'
import hsmj2Img from '@/assets/images/hsmj2.png'

const videoRef = ref(null)
const canvasRef = ref(null)
const cameraStarted = ref(false)
const selectedMask = ref(null)
const masks = ref([])
const photoDialogVisible = ref(false)
const capturedPhoto = ref('')
const currentMask = computed(() => masks.value.find((m) => String(m.id) === String(selectedMask.value)) || null)
let stream = null
let animationId = null
let detector = null
const maskImageCache = new Map()

const localMaskImages = {
  '白面具': whiteMaskImg,
  '蓝面具': blueMaskImg,
  '半黑半白面具': blackMaskImg,
  '黄面具': yellowMaskImg,
  '红/浅色调面具': redMaskImg,
  '黄色面具': yellowMaskImg,
  '白色面具': whiteMaskImg,
  '白色面具（经典款）': whitejdkImg,
  '半黑半白面具（阴阳脸）': blackMaskImg,
  '白色面具（火焰纹款）': whitehyImg,
  '红色面具': hsmjImg,
  '白色面具（精致款）': whitebsjzImg,
  '深蓝色面具': slsImg,
  '蓝色面具': lsmjImg,
  '深蓝色面具（精致款）': slsmjjzkImg,
  '蓝色面具（简约款）': lsmjjykImg,
  '黑色面具': hsmj2Img
}

const defaultMasks = [
  {
    id: 1,
    name: '白面具',
    image: whiteMaskImg,
    faceWidthRatio: 0.35,
    faceCenterYRatio: 0.37,
    description: '所属流派：白面具藏戏（最古老的藏戏流派，扎西雪巴等分支）\n\n核心特点：底色为白色，象征纯洁、温和、长寿，代表善良老者或仙女形象。早期用白山羊皮制作，保留山羊毛作须发，造型质朴简洁，是藏戏的源头流派。图中白面具带有红、绿装饰，属于白面具向蓝面具过渡的华丽变体（如扎西雪巴流派）。'
  },
  {
    id: 2,
    name: '蓝面具',
    image: blueMaskImg,
    faceWidthRatio: 0.46,
    faceCenterYRatio: 0.48,
    description: '所属流派：蓝面具藏戏（觉木隆、迥巴、香巴、江嘎尔四大流派）\n\n核心特点：底色为深蓝色，象征天空与湖水，代表勇敢、正义的猎人、渔夫（温巴角色）。头顶有日月徽记，装饰华丽，用布板糊裱、金丝点缀，风格古拙典雅。是藏戏中最具影响力的新派面具，代表剧目丰富，表演成熟。'
  },
  {
    id: 3,
    name: '半黑半白面具',
    image: blackMaskImg,
    faceWidthRatio: 0.62,
    faceCenterYRatio: 0.39,
    description: '所属流派：蓝面具藏戏（正戏角色面具）\n\n核心特点：半白半黑的阴阳脸，象征奸诈、两面三刀，代表巫师、反派丑角（如《苏吉尼玛》中的舞女亚玛更迫）。用强烈色彩对比表现人物性格，是藏戏中典型的以色喻人符号。'
  },
  {
    id: 4,
    name: '黄面具',
    image: yellowMaskImg,
    faceWidthRatio: 0.49,
    faceCenterYRatio: 0.44,
    description: '所属流派：白面具藏戏（扎西雪巴分支）/ 蓝面具藏戏（活佛、仙翁角色）\n\n核心特点：底色为金黄色，象征神圣、智慧、功德，代表活佛、喇嘛或仙翁角色。装饰繁复，边缘有白色卷草纹，耳饰华丽，风格庄严华贵，是白面具派的华丽升级款。'
  },
  {
    id: 5,
    name: '红/浅色调面具',
    image: redMaskImg,
    faceWidthRatio: 0.49,
    faceCenterYRatio: 0.35,
    description: '所属流派：藏戏正戏角色面具（或地方变体）\n\n核心特点：浅底色加红色额饰，红色象征威严、权力，浅色调保留温和感，可能代表贵族女性或地方小神。造型小巧精致，带有太阳状装饰，是藏戏面具的地方化、简化版本。'
  },
  {
    id: 6,
    name: '黄色面具',
    image: yellowMaskImg,
    faceWidthRatio: 0.49,
    faceCenterYRatio: 0.44,
    description: '🔍 核心身份与象征\n\n角色定位：黄色在藏戏面具中象征着神圣、威严与富贵，通常代表活佛、高僧、国王或地位尊崇的神明角色。\n\n文化寓意：黄色是藏传佛教中极其尊贵的颜色（如达赖、班禅的僧袍），选用黄色底色，意在赋予角色超凡的力量与至高无上的权威，同时也代表着智慧与吉祥。\n\n✨ 视觉元素解析\n\n面部底色：鲜亮的明黄色为主体，视觉冲击力极强，直接彰显角色的高贵身份。\n\n装饰细节：\n\n冠帽：头戴装饰繁复的尖顶冠帽，镶嵌红、蓝、绿等彩珠及宝石纹样，极尽华丽，是王室或神佛的典型标配。\n\n耳饰：佩戴硕大的圆形金质耳环，造型精致，延续了藏戏面具"重装饰"的艺术特征。\n\n纹饰：脸颊处有蓝色的花朵或图腾纹样，额头有天眼状的装饰，增添了神秘感与神圣感。\n\n须发：边缘环绕白色的火焰状须发，既增加了造型的层次感，也寓意着神圣与威严。'
  },
  {
    id: 7,
    name: '白色面具',
    image: whiteMaskImg,
    faceWidthRatio: 0.35,
    faceCenterYRatio: 0.37,
    description: '🔍 核心身份与象征\n\n角色定位：白色面具代表纯洁善良、温和敦厚的角色，通常是德高望重的长者、智者或善良的平民百姓，也象征长寿与理智。\n\n文化对比：与京剧白脸象征奸诈多疑不同，藏戏白色面具是善的化身，代表无害人之心、慈悲温和的品格。\n\n✨ 视觉元素解析\n\n面部底色：纯净的白色是核心，直接点明角色的善良本质。\n\n额部纹饰：红底白圈的日月纹，既是装饰，也象征福泽与智慧，是藏传佛教文化的体现。\n\n色彩搭配：红、绿、蓝等鲜艳色彩勾勒眉眼与唇须，对比强烈，符合藏戏面具夸张、鲜明的艺术风格。\n\n须发造型：飘逸的白色须发，强化了角色的长者形象与神圣感。'
  },
  {
    id: 8,
    name: '白色面具（经典款）',
    image: whitejdkImg,
    faceWidthRatio: 0.35,
    faceCenterYRatio: 0.37,
    description: '🔍 核心身份与象征\n\n角色定位：白色面具是藏戏中最具代表性的"善"脸谱，代表纯洁善良、温和敦厚的角色，通常是德高望重的长者、智者或平民百姓，也象征长寿与理智。\n\n文化寓意：白色在藏文化中是吉祥、纯净的颜色，与京剧"白脸奸臣"的寓意截然不同，代表角色无害人之心、慈悲温和的品格。\n\n✨ 视觉元素解析\n\n面部底色：纯净的白色为主体，直接点明角色的善良本质，红色轮廓线强化了造型的立体感与仪式感。\n\n额部纹饰：醒目的黄色日月纹是藏戏面具的经典符号，寓意光明、福泽与智慧，也呼应了藏传佛教的文化内涵。\n\n装饰细节：\n\n冠帽：红色弧形冠帽点缀银色四瓣花纹，造型简洁而富有民族特色。\n\n五官：眉眼线条细腻，蓝色彩绘胡须与红色唇线形成鲜明对比，风格精致而富有装饰性，清晰的双眼传递出角色的平和与智慧。'
  },
  {
    id: 9,
    name: '半黑半白面具（阴阳脸）',
    image: blackMaskImg,
    faceWidthRatio: 0.62,
    faceCenterYRatio: 0.39,
    description: '所属流派：蓝面具藏戏（正戏角色面具）\n\n核心特点：半白半黑的"阴阳脸"，象征奸诈、两面三刀，代表巫师、反派丑角（如《苏吉尼玛》中的舞女亚玛更迫）。用强烈色彩对比表现人物性格，是藏戏中典型的"以色喻人"符号。'
  },
  {
    id: 10,
    name: '白色面具（火焰纹款）',
    image: whitehyImg,
    faceWidthRatio: 0.35,
    faceCenterYRatio: 0.55,
    description: '🔍 核心身份与象征\n\n角色定位：白色面具是藏戏中最具代表性的通用脸谱，既可以代表纯洁善良、和蔼可亲的平民百姓，也可象征长寿、理智的长者，是"善"与"正义"的绝对化身。\n\n文化寓意：白色在藏文化中象征着纯洁与吉祥，与京剧"白脸奸臣"截然不同。面具闭合的双眼和沉稳的线条，传达出角色内心的平和与智慧。\n\n✨ 视觉元素解析\n\n造型设计：\n\n头部冠饰：采用极具张力的白色火焰状尖刺，这是该款面具的最大特征。在藏文化中，火焰纹象征着神圣、威严与驱除邪祟，同时也赋予角色一种超凡的精气神。\n\n面部轮廓：经典的倒三角脸型，搭配鲜艳的红色边框，与纯白底色形成强烈对比，视觉冲击力极强。\n\n额部纹饰：醒目的黄色日月纹是藏戏面具的标志性符号，寓意着"心如日月，光明磊落"。\n\n装饰细节：\n\n眉眼与须髯：蓝色的云纹状胡须、黑色的鼻梁线条以及红色的唇线，色彩明快且对比强烈，既保留了面具的夸张写意，又清晰勾勒出人物的面部特征。\n\n花纹点缀：红色弧形区域装饰有银色四瓣花，增添了精致感与民族特色。'
  },
  {
    id: 11,
    name: '红色面具',
    image: hsmjImg,
    faceWidthRatio: 0.49,
    faceCenterYRatio: 0.55,
    description: '🔍 核心身份与象征\n\n角色定位：红色面具在藏戏中代表威严、权力与炽烈性情，通常是国王、大将军、贵族或具有强大力量的神祇，象征着至高的权威与果敢的行动力。\n\n文化寓意：红色在藏文化中是力量、激情与吉祥的颜色，既可以代表正义的王权，也可表现角色刚烈暴躁的性格，与代表"善"的白色面具、代表"勇武"的蓝色面具形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：鲜亮的正红色是核心，视觉冲击力极强，直接点明角色的威严与力量感。\n\n额部纹饰：经典的黄色日月纹搭配红色圆点，是藏戏面具的标志性符号，寓意光明、福泽与神圣，也源自藏传佛教文化。\n\n装饰细节：\n\n冠帽：黑色弧形冠帽以黄色镶边，点缀银色四瓣花纹，造型简洁而华贵，符合王室或贵族身份。\n\n面部纹饰：黑色鼻梁与胡须线条明快，闭合的双眼与上扬的嘴角，既显沉稳威严，又带一丝亲和感，平衡了红色的炽烈。\n\n须发：米白色长须造型飘逸，强化了角色的长者或尊贵身份。'
  },
  {
    id: 12,
    name: '白色面具（精致款）',
    image: whitebsjzImg,
    faceWidthRatio: 0.35,
    faceCenterYRatio: 0.75,
    description: '🔍 核心身份与象征\n\n角色定位：白色面具代表纯洁善良、温和敦厚的角色，通常是德高望重的长者、智者或善良的平民百姓，也象征长寿与理智。\n\n文化寓意：白色在藏文化中是吉祥、纯净的颜色，代表无害人之心、慈悲温和的品格，与代表勇武的蓝色面具、代表尊贵的黄色面具形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：纯净的白色为主体，直接点明角色的善良本质。\n\n额部纹饰：黄色日月纹是藏戏面具的经典符号，寓意光明、福泽与智慧，也呼应了藏传佛教的文化内涵。\n\n装饰细节：\n\n冠帽：顶部冠帽采用华丽的织锦与珠饰纹样，绘有宗教人物形象，体现了藏戏面具与宗教文化的紧密关联，也彰显角色的庄重身份。\n\n五官：眉眼线条细腻，蓝色彩绘胡须与红色唇线形成鲜明对比，风格精致而富有装饰性。\n\n边缘：红色轮廓线勾勒出面部轮廓，强化了造型的立体感与仪式感。'
  },
  {
    id: 13,
    name: '深蓝色面具',
    image: slsImg,
    faceWidthRatio: 0.46,
    faceCenterYRatio: 0.65,
    description: '🔍 核心身份与象征\n\n角色定位：深蓝色（或蓝黑色）面具在藏戏中代表英勇无畏、忠诚刚正的角色，通常是勇士、英雄或护法神形象，象征力量与正义。\n\n文化寓意：蓝色底色呼应高原蓝天，也暗含"心如蓝天般开阔"的藏族精神内核，与白色面具的"善"、红色面具的"威严"形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：深邃的蓝色是核心，直接点明角色的勇武与刚毅。\n\n额部纹饰：橙黄色日月纹与银色圆点装饰，既借鉴了藏传佛教的符号体系，也寓意光明、洁净与神圣。\n\n色彩搭配：金、红、蓝等高饱和度色彩碰撞，符合藏戏面具夸张鲜明的艺术特征，强化了角色的英雄气概。\n\n装饰细节：两侧垂挂的彩条珠串、边缘的毛绒装饰，以及顶部华丽的冠帽，都体现了藏戏面具繁复精致的工艺传统，增添了仪式感与威严感。'
  },
  {
    id: 14,
    name: '蓝色面具',
    image: lsmjImg,
    faceWidthRatio: 0.46,
    faceCenterYRatio: 0.60,
    description: '🔍 核心身份与象征\n\n角色定位：蓝色面具在藏戏中代表英勇无畏、忠诚刚正的角色，通常是勇士、英雄或护法神形象，象征力量与正义。\n\n文化寓意：蓝色呼应高原蓝天，暗含"心如蓝天般开阔"的藏族精神内核，与白色面具的"善"、黄色面具的"尊贵"形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：深邃的蓝色是核心，直接点明角色的勇武与刚毅。\n\n额部纹饰：橙黄色日月纹与白色圆点装饰，借鉴了藏传佛教符号体系，寓意光明、洁净与神圣。\n\n装饰细节：顶部冠帽绘有象征佛法的彩色纹样，体现宗教文化内涵。\n\n耳饰：两侧垂挂十字形吉祥结与彩条流苏，是藏族传统吉祥符号，增添仪式感。\n\n面部纹饰：白色圆点与线条勾勒出简洁的五官，风格质朴而富有力量感。'
  },
  {
    id: 15,
    name: '深蓝色面具（精致款）',
    image: slsmjjzkImg,
    faceWidthRatio: 0.46,
    faceCenterYRatio: 0.75,
    description: '🔍 核心身份与象征\n\n角色定位：深蓝色面具在藏戏中代表英勇无畏、忠诚刚正的角色，通常是勇士、英雄或护法神形象，象征力量与正义。\n\n文化寓意：深邃的蓝色呼应高原的蓝天，暗含"心如蓝天般开阔"的藏族精神内核，代表角色刚毅、正直的品格，与白色面具的"善"、黄色面具的"尊贵"形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：深邃的蓝色是核心，直接点明角色的勇武与刚毅。\n\n额部纹饰：经典的黄色日月纹搭配白色圆点装饰，是藏戏面具的标志性符号，寓意光明、洁净与神圣，也源自藏传佛教文化。\n\n装饰细节：\n\n冠帽：顶部冠帽采用华丽的织锦与珠饰纹样，绘有宗教人物形象，体现了藏戏面具与宗教文化的紧密关联，也彰显角色的庄重身份。\n\n面部纹饰：白色圆点与线条勾勒出简洁的五官，搭配蓝色卷曲胡须，风格质朴而富有力量感，红色唇线增添了一丝灵动。\n\n边缘：白色轮廓线勾勒出面部轮廓，强化了造型的立体感与仪式感。'
  },
  {
    id: 16,
    name: '蓝色面具（简约款）',
    image: lsmjjykImg,
    faceWidthRatio: 0.46,
    faceCenterYRatio: 0.55,
    description: '🔍 核心身份与象征\n\n角色定位：蓝色面具在藏戏中代表英勇无畏、忠诚刚正的角色，通常是勇士、英雄或护法神形象，象征力量与正义。\n\n文化寓意：蓝色呼应高原蓝天，暗含"心如蓝天般开阔"的藏族精神内核，代表角色刚毅、正直的品格，与白色面具的"善"、黄色面具的"尊贵"形成鲜明区分。\n\n✨ 视觉元素解析\n\n面部底色：明亮的蓝色是核心，视觉冲击力强，直接点明角色的勇武气质。\n\n额部纹饰：黄色日月纹搭配红色圆点，是藏戏面具的经典符号，寓意光明、福泽与神圣，也源自藏传佛教文化。\n\n装饰细节：\n\n冠帽：橙色火焰状冠帽点缀白、黄、红圆点，造型简洁有力，充满活力与威严感。\n\n面部纹饰：黄色鼻饰、红色脸颊圆点与金色卷曲胡须，线条明快，风格质朴而富有装饰性，闭合的双眼更显沉静与威严。\n\n须发：米白色须发造型简洁，强化了角色的长者或英雄形象。'
  },
  {
    id: 17,
    name: '黑色面具',
    image: hsmj2Img,
    faceWidthRatio: 0.42,
    faceCenterYRatio: 0.75,
    description: '🔍 核心身份与象征\n\n角色定位：黑色面具在藏戏中代表威严、力量与正义，通常是护法神、勇猛的武将或具有震慑力的正面角色，象征着强大的守护力与刚正不阿的品格。\n\n文化寓意：黑色在藏文化中并非贬义，而是代表着威严、庄重与驱邪避凶，与代表"善"的白色面具、代表"勇武"的蓝色面具共同构成藏戏的核心脸谱体系。\n\n✨ 视觉元素解析\n\n面部底色：深邃的纯黑色是核心，视觉冲击力极强，直接点明角色的威严与力量感。\n\n额部纹饰：经典的黄色日月纹搭配白色圆点装饰，是藏戏面具的标志性符号，寓意光明、洁净与神圣，也源自藏传佛教文化。\n\n装饰细节：\n\n冠帽：顶部冠帽采用华丽的织锦与珠饰纹样，绘有宗教人物形象，体现了藏戏面具与宗教文化的紧密关联，也彰显角色的神圣身份。\n\n面部纹饰：白色圆点与线条勾勒出简洁的五官，搭配蓝色卷曲胡须与黄色鼻饰，风格质朴而富有力量感，红色唇线与白色轮廓线增添了灵动与立体感。'
  }
]

const defaultMaskMap = Object.fromEntries(defaultMasks.map((mask) => [mask.name, mask]))

const loadMaskImage = (src) => {
  return new Promise((resolve) => {
    if (maskImageCache.has(src)) {
      resolve(maskImageCache.get(src))
      return
    }
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      maskImageCache.set(src, img)
      resolve(img)
    }
    img.onerror = () => resolve(null)
    img.src = src
  })
}

onMounted(async () => {
  try {
    const res = await getMasks()
    masks.value = (res.data || []).map((m) => {
      const fallback = defaultMaskMap[m.name] || {}
      const local = localMaskImages[m.name]
      return {
        ...fallback,
        ...m,
        image: local || m.image || fallback.image,
        description: m.description || fallback.description || ''
      }
    })
    if (!masks.value.length) {
      masks.value = defaultMasks
    }
  } catch (error) {
    masks.value = defaultMasks
  }

  // 预加载所有面具图片
  for (const m of masks.value) {
    loadMaskImage(m.image)
  }
})

const startCamera = async () => {
  try {
    stream = await navigator.mediaDevices.getUserMedia({ 
      video: { facingMode: 'user', width: 640, height: 480 } 
    })
    const video = videoRef.value
    video.srcObject = stream
    cameraStarted.value = true
    
    video.onloadeddata = () => {
      const canvas = canvasRef.value
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      startDrawLoop()
    }
    
    ElMessage.success('摄像头已开启，正在加载人脸检测模型...')

    // 异步加载检测器
    try {
      if (!detector) {
        detector = await blazeface.load()
        ElMessage.success('人脸检测模型加载成功')
      }
    } catch (e) {
      ElMessage.warning('人脸检测模型加载失败，面具将显示在固定位置')
    }
  } catch (error) {
    ElMessage.error('无法访问摄像头，请检查权限设置')
  }
}

const stopCamera = () => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    videoRef.value.srcObject = null
    cameraStarted.value = false
    if (animationId) {
      clearTimeout(animationId)
      animationId = null
    }
    ElMessage.info('摄像头已关闭')
  }
}

// 当前面具位置（由检测循环更新，绘制循环读取）
const faceRect = { x: 0, y: 0, w: 0, h: 0, found: false, leftEye: null, rightEye: null }

const startDrawLoop = () => {
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  const video = videoRef.value

  // ---- 绘制循环：用setTimeout保证即使页面滚动也不会卡死 ----
  let drawRunning = true

  const draw = () => {
    if (!drawRunning) return

    try {
      if (cameraStarted.value && video && video.readyState >= 2) {
        // 画摄像头画面
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        if (selectedMask.value) {
          const mask = getCurrentMask()
          if (mask) {
            const maskImg = maskImageCache.get(mask.image)
            if (maskImg) {
              let maskX, maskY, maskW, maskH

              if (faceRect.found) {
                const faceCenterX = faceRect.x + faceRect.w / 2
                const faceCenterY = faceRect.y + faceRect.h / 2
                const fwr = mask.faceWidthRatio || 0.45
                const fcyr = mask.faceCenterYRatio || 0.45
                maskW = faceRect.w / fwr
                const ratio = maskImg.naturalHeight / maskImg.naturalWidth
                maskH = maskW * ratio
                maskX = faceCenterX - maskW / 2
                maskY = faceCenterY - maskH * fcyr
              } else {
                maskW = canvas.width * 0.4
                const ratio = maskImg.naturalHeight / maskImg.naturalWidth
                maskH = maskW * ratio
                maskX = (canvas.width - maskW) / 2
                maskY = canvas.height * 0.05
              }

              ctx.globalAlpha = 0.9
              ctx.drawImage(maskImg, maskX, maskY, maskW, maskH)
              ctx.globalAlpha = 1.0
            }
          }
        }
      }
    } catch (_) {}

    // 用setTimeout代替requestAnimationFrame，滚动不会卡死
    animationId = setTimeout(draw, 33)
  }

  draw()

  // ---- 检测循环：独立async，每次检测完更新faceRect ----
  const detectLoop = async () => {
    while (cameraStarted.value) {
      if (detector && video.readyState >= 2) {
        try {
          const faces = await detector.estimateFaces(video, false)
          if (faces && faces.length > 0) {
            const f = faces[0]
            const tl = Array.isArray(f.topLeft) ? f.topLeft : (f.topLeft.arraySync ? f.topLeft.arraySync() : [0, 0])
            const br = Array.isArray(f.bottomRight) ? f.bottomRight : (f.bottomRight.arraySync ? f.bottomRight.arraySync() : [0, 0])
            faceRect.x = tl[0]
            faceRect.y = tl[1]
            faceRect.w = br[0] - tl[0]
            faceRect.h = br[1] - tl[1]
            faceRect.found = true
            // BlazeFace landmarks: [右眼, 左眼, 鼻子, 嘴, 右耳, 左耳]
            if (f.landmarks && f.landmarks.length >= 2) {
              const toArr = (lm) => Array.isArray(lm) ? lm : (lm.arraySync ? lm.arraySync() : null)
              faceRect.rightEye = toArr(f.landmarks[0])
              faceRect.leftEye = toArr(f.landmarks[1])
            } else {
              faceRect.leftEye = null
              faceRect.rightEye = null
            }
          } else {
            faceRect.found = false
            faceRect.leftEye = null
            faceRect.rightEye = null
          }
        } catch (_) {
          faceRect.found = false
        }
      }
      // 每次检测间隔约100ms，避免CPU占用过高
      await new Promise(r => setTimeout(r, 100))
    }
  }

  detectLoop()
}

const changeMask = () => {
  const mask = currentMask.value
  if (mask) {
    loadMaskImage(mask.image)
  }
  ElMessage.success('脸谱已切换')
}

const getCurrentMask = () => {
  return currentMask.value
}

const capturePhoto = () => {
  const canvas = canvasRef.value
  capturedPhoto.value = canvas.toDataURL('image/png')
  photoDialogVisible.value = true
  ElMessage.success('拍照成功')
}

const downloadPhoto = () => {
  const link = document.createElement('a')
  link.download = `藏戏脸谱_${Date.now()}.png`
  link.href = capturedPhoto.value
  link.click()
  ElMessage.success('照片已下载')
}

onUnmounted(() => {
  stopCamera()
})
</script>

<style scoped lang="scss">
.vr-mask-container {
  background: white;
  border-radius: 10px;
  padding: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.control-panel {
  margin-bottom: 30px;
  
  h2 {
    color: #8B4513;
    margin-bottom: 5px;
    font-size: 24px;

    .title-tibetan {
      font-size: 16px;
      color: #A0522D;
      font-weight: 400;
      opacity: 0.7;
    }
  }

  .sub-title {
    color: #999;
    font-size: 13px;
    margin-bottom: 18px;
    line-height: 1.6;

    .tibetan {
      font-size: 13px;
      opacity: 0.8;
    }
  }

  .btn-tibetan {
    font-size: 13px;
    opacity: 0.8;
    margin-left: 4px;
  }
  
  .controls {
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;
  }
}

.display-area {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  
  .video-wrapper {
    position: relative;
    background: #000;
    border-radius: 10px;
    overflow: hidden;
    min-height: 360px;

    .hidden-video {
      position: absolute;
      top: 0;
      left: 0;
      width: 1px;
      height: 1px;
      opacity: 0;
      pointer-events: none;
    }
    
    canvas {
      width: 100%;
      display: block;
      border-radius: 10px;
    }

    .no-camera {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #666;
      height: 360px;

      p {
        margin-top: 15px;
        font-size: 16px;
        text-align: center;
        line-height: 1.6;

        .tibetan {
          font-size: 14px;
          opacity: 0.7;
        }
      }
    }
  }
  
  .mask-preview {
    background: #f9f9f9;
    padding: 20px;
    border-radius: 10px;
    max-height: 720px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    
    h3 {
      color: #8B4513;
      margin-bottom: 15px;
      font-size: 18px;

      .preview-tibetan {
        font-size: 14px;
        color: #A0522D;
        font-weight: 400;
        opacity: 0.7;
      }
    }
    
    img {
      width: 100%;
      border-radius: 8px;
      margin-bottom: 12px;
      max-height: 360px;
      object-fit: contain;
      background: #fff;
    }

    h4 {
      color: #333;
      font-size: 16px;
      margin-bottom: 10px;
    }
    
    .mask-description {
      color: #666;
      line-height: 1.6;
      white-space: pre-line;
      overflow-y: auto;
      padding-right: 6px;
    }
  }
}

@media (max-width: 1024px) {
  .display-area {
    grid-template-columns: 1fr;
  }
}
</style>
